<template>
  <b-modal
    :id="modalId"
    ref="my-modal"
    :title="isEditMode ? 'แก้ไขข้อมูลโครงการ' : 'เพิ่มข้อมูลโครงการ'"
    size="lg"
    :ok-title="isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'"
    :cancel-title="isSubmitting ? 'รอสักครู่...' : 'ยกเลิก'"
    :ok-disabled="isSubmitting"
    :cancel-disabled="isSubmitting"
    header-bg-variant="success"
    header-text-variant="white"
    body-class="px-3 py-2 modern-modal"
    content-class="modern-modal"
    hide-footer
    @show="onShow"
    @hidden="resetModal"
    @ok="handleOk"
  >
    <!-- Loading Overlay -->
    <div
      v-if="isSubmitting"
      class="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
      style="background: rgba(0,0,0,0.7); top: 0; left: 0; z-index: 1050;"
    >
      <div class="text-center">
        <b-spinner
          variant="success"
          class="mb-2"
        />
        <p class="text-white">
          กำลังประมวลผล...
        </p>
      </div>
    </div>

    <validation-observer
      ref="simpleRules"
      #default="{ invalid }"
    >
      <b-card
        no-body
        class="mb-0 border-0 bg-transparent"
      >
        <b-card-body class="p-2">
          <div class="d-flex align-items-center mb-3">
            <div class="modern-icon-circle bg-success-light mr-2">
              <feather-icon
                icon="InfoIcon"
                class="text-success"
                size="18"
              />
            </div>
            <h5 class="mb-0 text-success">
              ข้อมูลทั่วไป
            </h5>
          </div>
          <b-row>
            <!-- ชื่อโครงการ -->
            <b-col :lg="isEditMode ? 12 : 12">
              <b-form-group
                label="ชื่อโครงการ"
                label-for="pjname"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Project Name"
                  rules="required"
                >
                  <b-form-input
                    id="pj_name"
                    v-model="formData.pj_name"
                    :state="errors.length > 0 ? false:null"
                    required
                    type="text"
                    placeholder="ชื่อโครงการ"
                    :disabled="isSubmitting"
                    class="modern-input"
                  />
                  <small class="text-danger mt-1">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- รหัสโครงการ -->
            <b-col
              v-if="!isEditMode"
              :lg="7"
            >
              <b-form-group
                label="รหัสโครงการ"
                label-for="pj_code"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Project Code"
                  rules="required"
                >
                  <b-form-input
                    id="pjcode"
                    v-model="formData.pj_code"
                    :state="errors.length > 0 ? false:null"
                    required
                    type="text"
                    placeholder="รหัสโครงการ"
                    :disabled="isSubmitting"
                    class="modern-input"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- รหัสโครงการ (แบบแก้ไข) -->
            <b-col
              v-if="isEditMode"
              :lg="7"
            >
              <b-form-group
                label="รหัสโครงการ"
                label-for="pj_code"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Project Code"
                  rules="required"
                >
                  <b-form-input
                    id="pjcode"
                    v-model="formData.pj_code"
                    :state="errors.length > 0 ? false:null"
                    required
                    :readonly="isEditMode"
                    type="text"
                    placeholder="รหัสโครงการ"
                    :disabled="isSubmitting"
                    class="modern-input"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- เลขที่สัญญา -->
            <b-col :lg="5">
              <b-form-group
                label="เลขที่สัญญา"
                label-for="contract_no"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Contract No"
                  rules="required"
                >
                  <b-form-input
                    id="contractno"
                    v-model="formData.contract_no"
                    :state="errors.length > 0 ? false:null"
                    required
                    type="text"
                    placeholder="เลขที่สัญญา"
                    :disabled="isSubmitting"
                    class="modern-input"
                  />
                  <small class="text-danger mt-1">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- หน่วยงานที่รับผิดชอบ -->
            <b-col :lg="7">
              <b-form-group
                label="หน่วยงานที่รับผิดชอบ"
                label-for="department"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Department"
                  rules="required"
                >
                  <v-select
                    v-model="selectDepartment"
                    :reduce="department => department"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :state="errors.length > 0 ? false:null"
                    required
                    label="deptname"
                    :options="department"
                    placeholder="เลือกหน่วยงาน..."
                    transition=""
                    :disabled="isSubmitting"
                    class="full-width-select"
                    @input="addDepartment()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- สายทาง -->
            <b-col :lg="5">
              <b-form-group
                label="สายทาง"
                label-for="road_id"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Road"
                  rules="required"
                >
                  <v-select
                    v-model="selectRoad"
                    :reduce="road => road"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :state="errors.length > 0 ? false:null"
                    required
                    label="wayid"
                    :options="road"
                    placeholder="เลือกสายทาง..."
                    transition=""
                    :disabled="isSubmitting"
                    class="full-width-select"
                    @input="addRoad()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- ผู้รับจ้าง -->
            <b-col
              v-if="!isEditMode"
              lg="12"
            >
              <b-form-group
                label="ผู้รับจ้าง"
                label-for="vendor_name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Vendor"
                  rules="required"
                >
                  <v-select
                    v-model="vendorModel"
                    :reduce="vendor => vendor.vendor_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :state="errors.length > 0 ? false:null"
                    required
                    label="vendor_name"
                    :options="vendor"
                    placeholder="เลือกผู้รับจ้าง..."
                    transition=""
                    :disabled="isSubmitting"
                    class="full-width-select"
                    @input="addVendor()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- ผู้รับจ้าง (แบบแก้ไข) -->
            <b-col
              v-if="isEditMode"
              lg="12"
            >
              <b-form-group
                label="ผู้รับจ้าง"
                label-for="vendor_name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Vendor"
                  rules="required"
                >
                  <v-select
                    v-model="vendorModel"
                    :reduce="vendor => vendor.vendor_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :state="errors.length > 0 ? false:null"
                    required
                    label="vendor_name"
                    :options="vendor"
                    placeholder="เลือกผู้รับจ้าง..."
                    transition=""
                    :disabled="isSubmitting"
                    class="full-width-select"
                    @input="addVendor()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- แถวใหม่สำหรับวันที่รับประกันและวันสิ้นสุดค้ำประกัน -->
          <b-row>
            <!-- วันที่รับประกัน -->
            <b-col lg="6">
              <b-form-group
                label="วันที่รับประกัน"
                label-for="guarantee date start"
              >
                <validation-provider
                  #default="{ errors }"
                  name="guarantee date start"
                  rules="required"
                >
                  <b-form-datepicker
                    id="guarantee_date_start"
                    v-model="date.guarantee_date_start"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    :disabled="isSubmitting"
                    placeholder="เลือกวันที่"
                    locale="th"
                    today-button
                    reset-button
                    close-button
                    class="modern-datepicker"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- วันสิ้นสุดค้ำประกัน -->
            <b-col lg="6">
              <b-form-group
                label="วันสิ้นสุดค้ำประกัน"
                label-for="guarantee date end"
              >
                <validation-provider
                  #default="{ errors }"
                  name="guarantee date end"
                  rules="required"
                >
                  <b-form-datepicker
                    id="guarantee-date-end"
                    v-model="date.guarantee_date_end"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    :disabled="isSubmitting"
                    placeholder="เลือกวันที่"
                    locale="th"
                    today-button
                    reset-button
                    close-button
                    class="modern-datepicker"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Custom Footer -->
      <div class="modal-custom-footer">
        <div class="d-flex justify-content-between w-100">
          <button
            class="btn btn-danger"
            :disabled="isSubmitting"
            @click="$bvModal.hide(modalId)"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
            {{ isSubmitting ? 'รอสักครู่...' : 'ยกเลิก' }}
          </button>
          <button
            class="btn btn-success"
            :disabled="isSubmitting || invalid"
            @click="handleOk"
          >
            <feather-icon
              icon="CheckIcon"
              size="16"
            />
            <b-spinner
              v-if="isSubmitting"
              small
              class="ml-1"
            />
            {{ isSubmitting ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </validation-observer>
  </b-modal>
</template>

<script>
import axios from '@axios'
import {
  BRow, BCol, BFormGroup, BFormInput, BFormDatepicker, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// ลงทะเบียน validator
extend('required', {
  ...required,
  message: 'กรุณากรอกข้อมูลในช่องนี้',
})

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormDatepicker,
    BSpinner,
  },
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isSubmitting: false,
      formData: {
        pj_id: '',
        pj_name: '',
        pj_code: '',
        contract_no: '',
        deptid: '',
        deptname: '',
        road_id: '',
        road_name: '',
        vendor_id: '',
        vendor_shortname: '',
        guarantee_year: '',
        guarantee_date_start: '',
        guarantee_date_end: '',
        groupname: '',
        pj_value: '',
      },
      date: {
        guarantee_date_start: '',
        guarantee_date_end: '',
      },
      selectDepartment: null,
      selectRoad: null,
      department: [],
      road: [],
      vendor: [],
      userData: this.$store.state.userData,
      codeFeedbackHelper: '',
      selectVendor: null,
    }
  },
  computed: {
    vendorModel: {
      get() {
        if (this.isEditMode) {
          // ในโหมดแก้ไข ค้นหา vendor จาก vendor_id ที่มีอยู่
          return this.vendor.find(v => v.vendor_id === this.formData.vendor_id) || null
        }
        return this.selectVendor
      },
      set(value) {
        if (this.isEditMode) {
          if (value) {
            this.formData.vendor_id = value.vendor_id
            this.formData.vendor_shortname = value.vendor_shortname
          }
        } else {
          this.selectVendor = value
        }
      },
    },
  },
  mounted() {
    this.dropdownDepartment()
    this.dropdownRoad()
    this.dropdownVendor()
    console.log('userData:', this.userData)
  },
  methods: {
    onShow() {
      if (this.isEditMode) {
        this.getProjectById()
      } else {
        this.genCodePJ()
      }
    },

    resetModal() {
      this.formData = {
        pj_id: '',
        pj_name: '',
        pj_code: '',
        contract_no: '',
        deptid: '',
        deptname: '',
        road_id: '',
        road_name: '',
        vendor_id: '',
        vendor_shortname: '',
        guarantee_year: '',
        guarantee_date_start: '',
        guarantee_date_end: '',
        groupname: '',
        pj_value: '',
      }
      this.date = {
        guarantee_date_start: '',
        guarantee_date_end: '',
      }
      this.selectDepartment = null
      this.selectRoad = null
      this.selectVendor = null
      this.isSubmitting = false
    },

    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault()
      // Trigger submit handler
      this.validationForm()
    },

    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (!success) {
          // eslint-disable-next-line
          return
        }

        // แสดง confirm dialog ก่อนบันทึก
        this.$swal({
          title: '<span style="color:#000000">ต้องการบันทึกข้อมูลหรือไม่?</span>',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'บันทึก',
          cancelButtonText: 'ยกเลิก',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        }).then(result => {
          if (result.value) {
            if (this.isEditMode) {
              this.editInfo()
            } else {
              this.addInfo()
            }
          }
        })
      })
    },

    // สร้างรหัสโครงการอัตโนมัติ
    genCodePJ() {
      if (!this.userData) return

      axios
        .post('/genProjectCode', {
          vendor_id: this.userData.vendor_id,
          groupname: this.userData.groupname,
        })
        .then(response => {
          this.formData.pj_code = response.data.new_pj_code
        })
        .catch(error => {
          console.log(error)
        })
    },

    // ดึงข้อมูลโครงการตาม ID
    getProjectById() {
      axios
        .post('/fetchProject', { pj_id: this.projectId })
        .then(response => {
          this.formData.pj_name = response.data.pj_name
          this.formData.contract_no = response.data.contract_no
          this.date.guarantee_date_start = response.data.guarantee_date_start
          this.date.guarantee_date_end = response.data.guarantee_date_end
          this.formData.pj_code = response.data.pj_code
          this.formData.pj_value = response.data.pj_value
          this.formData.pj_id = response.data.pj_id
          this.selectDepartment = response.data.deptname
          this.formData.deptname = response.data.deptname
          this.formData.deptid = response.data.deptid
          this.formData.road_id = response.data.road_id
          this.formData.road_name = response.data.road_name
          this.formData.vendor_id = response.data.vendor_id
          this.selectRoad = response.data.road_name
        })
        .catch(error => {
          console.log(error)
        })
    },

    // แก้ไขข้อมูลโครงการ
    async editInfo() {
      if (!this.checkRequiredFields()) {
        this.showError('กรุณากรอกข้อมูลให้ครบถ้วน')
        return
      }

      this.isSubmitting = true

      try {
        this.formData.guarantee_date_end = this.date.guarantee_date_end
        this.formData.guarantee_date_start = this.date.guarantee_date_start

        // ตรวจสอบวันที่ค้ำประกัน
        const startDate = new Date(this.date.guarantee_date_start)
        const endDate = new Date(this.date.guarantee_date_end)

        if (endDate <= startDate) {
          this.showError('วันสิ้นสุดค้ำประกันต้องมากกว่าวันเริ่มต้นค้ำประกัน')
          this.isSubmitting = false
          return
        }

        this.getYearDiff(startDate, endDate)

        await axios.post('/editProject', this.formData)

        this.$swal({
          icon: 'success',
          title: '<span style="color:#000000">แก้ไขข้อมูลเรียบร้อย.</span>',
          showConfirmButton: false,
          timer: 1000,
        })

        this.$nextTick(() => {
          this.$bvModal.hide(this.modalId)
        })

        this.$emit('fetch')
      } catch (error) {
        console.log(error)
        this.handleNetworkError(error)
      } finally {
        this.isSubmitting = false
      }
    },

    // เพิ่มข้อมูลโครงการใหม่
    async addInfo() {
      if (!this.checkRequiredFields()) {
        this.showError('กรุณากรอกข้อมูลให้ครบถ้วน')
        return
      }

      this.isSubmitting = true

      try {
        this.formData.guarantee_date_end = this.date.guarantee_date_end
        this.formData.guarantee_date_start = this.date.guarantee_date_start
        if (this.userData) {
          this.formData.groupname = this.userData.groupname
        }

        // ตรวจสอบวันที่ค้ำประกัน
        const startDate = new Date(this.date.guarantee_date_start)
        const endDate = new Date(this.date.guarantee_date_end)

        if (endDate <= startDate) {
          this.showError('วันสิ้นสุดค้ำประกันต้องมากกว่าวันเริ่มต้นค้ำประกัน')
          this.isSubmitting = false
          return
        }

        this.getYearDiff(startDate, endDate)

        await axios.post('/addProject', this.formData)

        this.$swal({
          icon: 'success',
          title: '<span style="color:#000000">บันทึกข้อมูลเรียบร้อย.</span>',
          showConfirmButton: false,
          timer: 1000,
        })

        this.$nextTick(() => {
          this.$bvModal.hide(this.modalId)
        })

        this.$emit('fetch')
      } catch (error) {
        console.log(error)
        this.handleNetworkError(error)
      } finally {
        this.isSubmitting = false
      }
    },

    // คำนวณจำนวนปีระหว่างวันที่
    getYearDiff(date1, date2) {
      this.formData.guarantee_year = Math.abs(date2.getFullYear() - date1.getFullYear())
    },

    // ดึงข้อมูล dropdown
    dropdownDepartment() {
      axios
        .post('/dropdownDepartment')
        .then(response => {
          this.department = response.data
        })
    },

    dropdownRoad() {
      axios
        .post('/dropdownRoad')
        .then(response => {
          this.road = response.data
        })
    },

    dropdownVendor() {
      axios
        .post('/dropdownVendor')
        .then(response => {
          this.vendor = response.data
        })
    },

    // เพิ่มข้อมูลเมื่อเลือกจาก dropdown
    addDepartment() {
      if (!this.selectDepartment) return
      this.formData.deptid = this.selectDepartment.DeptID
      this.formData.deptname = this.selectDepartment.deptname
    },

    addRoad() {
      if (!this.selectRoad) return
      this.formData.road_id = this.selectRoad.id
      this.formData.road_name = this.selectRoad.wayid
    },

    addVendor() {
      if (!this.vendorModel) return
      // หาข้อมูลผู้รับจ้างจาก vendor_id
      const selectedVendor = this.vendor.find(v => v.vendor_id === this.vendorModel)
      if (selectedVendor) {
        this.formData.vendor_id = selectedVendor.vendor_id
        this.formData.vendor_shortname = selectedVendor.vendor_shortname
      }
    },

    showError(message) {
      this.$swal({
        icon: 'error',
        text: message,
        showConfirmButton: true,
        confirmButtonText: 'ตกลง',
      })
    },

    // ตรวจสอบว่าฟิลด์ที่จำเป็นถูกกรอกครบหรือไม่
    checkRequiredFields() {
      const requiredFields = [
        'pj_name',
        'pj_code',
        'contract_no',
        'deptid',
        'road_id',
        'vendor_id',
      ]

      return requiredFields.every(field => {
        const value = this.formData[field]
        return value !== null && value !== undefined && value !== ''
      })
    },

    // จัดการข้อผิดพลาดจากเครือข่าย
    handleNetworkError(error) {
      if (!navigator.onLine) {
        this.showError('ไม่สามารถเชื่อมต่ออินเทอร์เน็ตได้ กรุณาตรวจสอบการเชื่อมต่อ')
        return
      }

      if (error.code === 'ECONNABORTED') {
        this.showError('การเชื่อมต่อใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง')
        return
      }

      if (error.response) {
        // Server responded with error
        const { status } = error.response
        const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง'

        switch (status) {
          case 400:
            this.showError(errorMessage || 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลที่กรอก')
            break
          case 401:
            this.showError('กรุณาเข้าสู่ระบบใหม่')
            // Optional: Redirect to login
            break
          case 403:
            this.showError('คุณไม่มีสิทธิ์ดำเนินการนี้')
            break
          case 404:
            this.showError('ไม่พบข้อมูลที่ต้องการ')
            break
          case 500:
            this.showError('เกิดข้อผิดพลาดจากระบบ กรุณาลองใหม่ภายหลัง')
            break
          default:
            this.showError(errorMessage)
        }
      } else {
        // Network error or other issues
        this.showError('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง')
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// สไตล์สำหรับ v-select ที่กว้างเต็ม container
::v-deep .full-width-select {
  width: 100% !important;

  .vs__dropdown-toggle {
    width: 100% !important;
  }

  .vs__selected-options {
    width: 100% !important;
    padding: 0;
  }

  .vs__search {
    width: 100% !important;
  }
}

// แก้ไขเพิ่มเติมสำหรับ v-select ในโหมด dark
::v-deep .v-select {
  width: 100%;

  .vs__dropdown-toggle {
    width: 100%;
    background-color: #1e1e2d;
    border: 1px solid #2d2d3f;
    border-radius: 0.5rem;
    padding: 0.5rem;

    &:focus, &:hover {
      border-color: #28c76f;
    }
  }

  .vs__selected {
    color: #fff;
  }

  .vs__search {
    color: #fff;
  }

  .vs__actions {
    padding-right: 0.5rem;
  }

  .vs__dropdown-menu {
    background-color: #1e1e2d;
    border: 1px solid #2d2d3f;

    .vs__dropdown-option {
      color: #fff;

      &--highlight {
        background-color: #28c76f;
        color: #fff;
      }
    }
  }
}

// สไตล์สำหรับ input แบบใหม่
.modern-input {
  background-color: #1e1e2d;
  border: 1px solid #2d2d3f;
  border-radius: 0.5rem;
  color: #fff;
  padding: 0.5rem;
  width: 100%;

  &:focus {
    border-color: #28c76f;
    box-shadow: none;
  }

  &::placeholder {
    color: #6c757d;
  }
}

// สไตล์สำหรับ datepicker แบบใหม่
.modern-datepicker {
  background-color: #1e1e2d;
  border: 1px solid #2d2d3f;
  border-radius: 0.5rem;
  color: #fff;
  width: 100%;

  ::v-deep .b-form-datepicker {
    background-color: #1e1e2d;

    .btn {
      background-color: #1e1e2d;
      color: #fff;

      &:hover {
        background-color: #28c76f;
      }
    }
  }
}

// สไตล์สำหรับ modal overlay
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.text-danger {
  font-size: 80%;
  color: #dc3545;
}
</style>
